import { APIClient } from '../../helpers/api_helper';
import secureApi from '../../utils/secureApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const api = new APIClient();

export interface TeamData {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageCategory: string;
  createdAt: string;
  playersCount: number;
  approvedPlayersCount: number;
  paidPlayerIds?: string[]; // New field for paid player IDs
  paidPlayersCount?: number; // New field for paid players count
  registration?: string | null; // New field for existing registration ID
  // ✅ ADDED: Payment fields from API response
  totalAmount?: number;
  amountPaid?: number;
  balanceAmount?: number;
}

export interface ListTeamsResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    teams: TeamData[];
  };
}

export interface CreateTeamRequest {
  name: string;
  shortName: string;
  logo: string;
  managerName: string;
  countryCode: number;
  managerContactNum: number;
  location: string;
  ageCategory: string[];
  tournament?: string; // Optional tournament ID for tournament-specific team creation
}

export interface CreateTeamResponse {
  success: boolean;
  message: string;
  data?: any;
}

// =========================
// Team Information APIs
// =========================

// Fetch teams by club ID (old API) - Updated to support tournament ID
export const getTeamsByClubId = async (clubId: string, tournamentId?: string): Promise<TeamData[]> => {
  try {
    // Build URL with optional tournament ID
    let url = `${API_BASE_URL}/api/v1/club/list-teams/${clubId}`;
    if (tournamentId) {
      url += `?tournament=${tournamentId}`;
    }
    
    // APIClient handles token management automatically
    const response = await api.get(url, null);

    
    // Handle the API response structure
    if (response && response.data && response.data.teams) {
      return response.data.teams as TeamData[];
    }
    
    // Fallback if response structure is different
    if (response && (response as any).teams) {
      return (response as any).teams as TeamData[];
    }
    
    throw new Error('Invalid response structure from teams API');
  } catch (error) {
    console.error('Failed to fetch teams:', error);
    
    // If it's a token-related error, provide more specific guidance
    if (typeof error === 'string' && error.includes('Token')) {
      console.error('Token-related error detected. Please check authentication.');
    }
    
    throw error;
  }
};

// Fetch teams by club ID (new API) - Updated to support tournament ID
export const fetchTeamsByClubId = async (clubId: string, tournamentId?: string): Promise<TeamData[]> => {
  try {
    console.log('🔍 Fetching teams using new API for club ID:', clubId);
    
    // Build URL with optional tournament ID
    let url = `${API_BASE_URL}/api/v1/club/fetch-teams/${clubId}`;
    if (tournamentId) {
      url += `?tournament=${tournamentId}`;
    }
    
    // APIClient handles token management automatically
    const response = await api.get(url, null);
    
    // Handle the API response structure
    if (response && response.data && response.data.teams) {
      console.log('🔍 Teams found:', response.data.teams.length);
      return response.data.teams as TeamData[];
    }
    
    // Fallback if response structure is different
    if (response && (response as any).teams) {
      console.log('🔍 Teams found (fallback):', (response as any).teams.length);
      return (response as any).teams as TeamData[];
    }
    
    console.log('🔍 No teams found or invalid response structure');
    return [];
  } catch (error) {
    console.error('❌ Failed to fetch teams with new API:', error);
    throw error;
  }
}; 

/**
 * Create a new team for a club
 * @param clubId - The club ID
 * @param teamData - Team data to create
 * @returns Promise<CreateTeamResponse>
 */
export const createTeam = async (clubId: string, teamData: CreateTeamRequest): Promise<CreateTeamResponse> => {
  try {
    if (!API_BASE_URL) {
      return {
        success: false,
        message: 'API_BASE_URL not configured'
      };
    }
    
    // Use secureApi for automatic token management
    const response = await secureApi.post<any>(
      `/api/v1/club/add-team/${clubId}`,
      teamData
    );

    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (response as any)?.data || response;

    return {
      success: true,
      message: 'Team created successfully',
      data: responseData
    };
  } catch (error: any) {
    console.error('Failed to create team:', error);
    
    // Extract the specific error message from API response
    let errorMessage = 'Failed to create team. Please try again.';
    
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      message: errorMessage
    };
  }
}; 

/**
 * Get age categories from tournaments
 * @param tournamentId - Optional tournament ID to get age categories for a specific tournament
 * @returns Promise<string[]> - Array of unique age categories
 */
export const getAgeCategoriesFromTournaments = async (tournamentId?: string): Promise<string[]> => {
  try {
    // If tournamentId is provided, fetch that specific tournament
    if (tournamentId) {
      try {
        const response = await secureApi.get<any>(
          `/api/v1/tournaments/manage/tournament-info/${tournamentId}`
        );

        // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
        let tournament = null;
        if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
          tournament = (response as any)?.data || (response as any)?.tournament || response;
        } else if ((response as any)?.tournament) {
          tournament = (response as any).tournament;
        } else {
          tournament = response;
        }

        if (tournament && tournament.ageCategories && Array.isArray(tournament.ageCategories)) {
          return tournament.ageCategories.sort();
        } else {
          console.warn('Tournament found but no age categories available');
          return [];
        }
      } catch (tournamentError: any) {
        console.error('Failed to fetch specific tournament:', tournamentError);
        console.error('Error response:', tournamentError.response?.data);
        console.error('Error status:', tournamentError.response?.status);
        // Fallback to default categories if specific tournament fetch fails
        return ['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18'];
      }
    }

    // If no tournamentId provided, fetch all upcoming tournaments (existing behavior)
    const response = await secureApi.get<any>(
      `/api/v1/tournament-registration/list-all-tournaments`,
      {
        params: { status: 'upcoming' }
      }
    );

    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    let tournaments = [] as any[];
    if ((response as any)?.statusCode === 200 && (response as any)?.status === 'success') {
      tournaments = (response as any)?.data?.tournaments || (response as any)?.tournaments || [];
    } else if ((response as any)?.tournaments) {
      tournaments = (response as any).tournaments;
    } else if (Array.isArray(response)) {
      tournaments = response;
    } else {
      console.error('Unexpected response structure:', response);
      throw new Error((response as any).message || 'Failed to fetch tournaments');
    }

    // Extract all age categories from tournaments
    const allAgeCategories = tournaments.reduce((acc: string[], tournament: any) => {
      if (tournament.ageCategories && Array.isArray(tournament.ageCategories)) {
        acc.push(...tournament.ageCategories);
      }
      return acc;
    }, []);

    // Remove duplicates and sort
    const uniqueAgeCategories = Array.from(new Set(allAgeCategories)).sort() as string[];
    
    return uniqueAgeCategories;
  } catch (error: any) {
    console.error('Failed to fetch age categories from tournaments:', error);
    // Return default age categories if API fails
    return ['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18'];
  }
};

export interface UpdateTeamRequest {
  name: string;
  shortName: string;
  logo: string;
  managerName: string;
  countryCode: number;
  managerContactNum: number;
  location: string;
  ageCategory: string; // Changed from string[] to string to match API response
  club?: string; // Club ID for team update (optional for backward compatibility)
}

export interface UpdateTeamResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const updateTeam = async (teamId: string, teamData: UpdateTeamRequest): Promise<UpdateTeamResponse> => {
  try {
    const response = await secureApi.post<any>(
      `/api/v1/club/update-team-details/${teamId}`,
      teamData
    );

    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (response as any)?.data || response;

    return {
      success: true,
      message: 'Team updated successfully',
      data: responseData
    };
  } catch (error: any) {
    console.error('Failed to update team:', error);
    console.error('Error response:', error.response);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Error statusText:', error.response?.statusText);
    
    // Handle different error response structures
    if (error.response?.data) {
      const errorData = error.response.data;
      console.log('Error data structure:', errorData);
      console.log('Error data keys:', Object.keys(errorData));
      
      // Check for error field first (as per your example)
      if (errorData.error) {
        console.log('✅ Found error field:', errorData.error);
        const errorMessage = errorData.error;
        console.log('🚀 Throwing error with message:', errorMessage);
        throw new Error(errorMessage);
      }
      
      // Fallback to message field
      if (errorData.message) {
        console.log('✅ Found message field:', errorData.message);
        throw new Error(errorData.message);
      }
      
      // Fallback to status message
      if (errorData.statusMessage) {
        console.log('✅ Found statusMessage field:', errorData.statusMessage);
        throw new Error(errorData.statusMessage);
      }
      
      // Check for nested error structures
      if (errorData.data && errorData.data.error) {
        console.log('✅ Found nested error field:', errorData.data.error);
        throw new Error(errorData.data.error);
      }
      
      if (errorData.data && errorData.data.message) {
        console.log('✅ Found nested message field:', errorData.data.message);
        throw new Error(errorData.data.message);
      }
      
      // Check if the entire response data is a string (some APIs return error as string)
      if (typeof errorData === 'string') {
        console.log('✅ Found string error:', errorData);
        throw new Error(errorData);
      }
      
      console.log('❌ No error message found in response data');
    } else {
      console.log('❌ No response data found');
    }
    
    // Only throw generic error if no specific error message was found
    if (error.message && error.message !== 'Network Error') {
      console.log('✅ Re-throwing original error:', error.message);
      throw error; // Re-throw the original error if it has a message
    }
    
    console.log('❌ Throwing generic error');
    throw new Error('Failed to update team. Please try again.');
  }
};

export interface TeamDetails {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  managerName: string;
  countryCode: number;
  managerContactNum: number;
  location: string;
  ageCategory: string[] | string; // Can be either array or string based on API response
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  clubInfo?: {
    _id: string;
    name: string;
    logo: string;
  };
}

export const getTeamById = async (teamId: string): Promise<TeamDetails> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    console.log('Fetching team details for ID:', teamId);
    console.log('API URL:', `/api/v1/club/team/find-by-id/${teamId}`);

    // Try multiple possible endpoints
    const endpoints = [
      `/api/v1/club/team/find-by-id/${teamId}`,
      `/api/v1/club/teams/${teamId}`,
      `/api/v1/teams/${teamId}`,
      `/api/v1/club/team/${teamId}`
    ];

    let response: any = null;
    let lastError: any = null;

    for (const endpoint of endpoints) {
      try {
        console.log('Trying endpoint:', endpoint);
        response = await secureApi.get<any>(endpoint);
        
        if (response) {
          console.log('Success with endpoint:', endpoint);
          console.log('API Response:', response);
          break;
        }
      } catch (endpointError: any) {
        console.log('Failed with endpoint:', endpoint, endpointError.message);
        lastError = endpointError;
        continue;
      }
    }

    if (!response) {
      throw lastError || new Error('All endpoints failed');
    }

    // Handle different response structures
    if ((response as any)?.success && (response as any)?.data) {
      return (response as any).data;
    } else if ((response as any)?.data) {
      return (response as any).data;
    } else if ((response as any)?.status === 'success' && (response as any)?.data) {
      return (response as any).data;
    } else if ((response as any)?.statusCode === 200 && (response as any)?.data) {
      return (response as any).data;
    } else if (response && typeof response === 'object' && (response as any)._id) {
      // Direct team object
      return response;
    } else {
      console.error('Unexpected response structure:', response);
      throw new Error('Team not found or invalid response structure');
    }
  } catch (error: any) {
    console.error('Failed to fetch team by ID:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    if (error.response?.status === 404) {
      throw new Error('Team not found');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to view this team.');
    } else if (error.code === 'NETWORK_ERROR') {
      throw new Error('Network error. Please check your internet connection.');
    } else {
      // Handle different error response structures
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for error field first (as per your example)
        if (errorData.error) {
          throw new Error(errorData.error);
        }
        
        // Fallback to message field
        if (errorData.message) {
          throw new Error(errorData.message);
        }
        
        // Fallback to status message
        if (errorData.statusMessage) {
          throw new Error(errorData.statusMessage);
        }
      }
      
      throw new Error(error.message || 'Failed to fetch team details');
    }
  }
}; 

/**
 * Delete a team
 * @param teamId - The team ID
 * @returns Promise<boolean>
 */
export const deleteTeam = async (teamId: string): Promise<boolean> => {
  try {
    console.log('Sending delete request for teamId:', teamId);
    const response = await secureApi.post<any>(
      `/api/v1/club/delete-team/${teamId}`,
      {} // Empty body for POST request
    );

    console.log('Delete team response:', response);
    
    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (response as any)?.data || response;

    // For delete operations, check response structure for success
    if ((response as any)?.statusCode === 200 || (response as any)?.status === 'success' || responseData) {
      return true;
    }

    console.log('❌ Response indicates failure, returning false');
    return false;
  } catch (error: any) {
    console.error('❌ Exception in deleteTeam:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw new Error(error.response?.data?.message || 'Failed to delete team');
  }
}; 